// Constantes + helpers génériques
export const CHUNK = 60;

export const sortIcon = (order) =>
  order === "none" ? "⇅" : order === "asc" ? "▲" : "▼";

export function neighborsFromSeason(season) {
  const m = /^(\d{4})\s*-\s*(\d{4})$/.exec(season || "");
  if (!m) return [season].filter(Boolean);
  const y1 = parseInt(m[1], 10);
  const y2 = parseInt(m[2], 10);
  return [`${y1 - 1}-${y2 - 1}`, `${y1}-${y2}`, `${y1 + 1}-${y2 + 1}`];
}

export function getStatusColor(statut) {
  switch (statut) {
    case "Licencié":
      return "bg-green-300 text-slate-800";
    case "Licencié Hors Club":
      return "bg-green-200 text-slate-800";
    case "Inscrit":
      return "bg-yellow-300 text-slate-900";
    case "Cours d'essai":
      return "bg-red-300 text-slate-900";
    case "Licencié - Arrêt":
    case "Ancien Licencié et Arrêt":
      return "bg-slate-300 text-slate-800";
    case "Ancien Licencié":
      return "bg-slate-900 text-white";
    case "Non Licencié - Arrêt":
      return "bg-slate-500 text-white";
    default:
      return "bg-slate-200 text-slate-800";
  }
}

/* =========================
   Couleurs de groupes
   ========================= */

// Si jamais un label est stocké (rare), on mappe -> classe Tailwind
const LABEL_TO_BG = {
  Bleu: "bg-blue-400",
  Vert: "bg-green-400",
  Jaune: "bg-yellow-400",
  Rouge: "bg-red-400",
  Indigo: "bg-indigo-400",
  Violet: "bg-purple-400",
  Rose: "bg-pink-400",
  Orange: "bg-orange-400",
  Cyan: "bg-cyan-400",
  Teal: "bg-teal-400",
  Emerald: "bg-emerald-400",
  Marron: "bg-amber-600",
  Turquoise: "bg-cyan-500",
  Gris: "bg-gray-500",
  Lime: "bg-lime-400",
  Fuchsia: "bg-fuchsia-500",
  Sky: "bg-sky-400",
  Zinc: "bg-zinc-400",
};

// Map "400 -> 200" pour les fonds clairs (ta technique)
const LIGHT_BG_MAP = {
  "bg-orange-400": "bg-orange-200",
  "bg-indigo-400": "bg-indigo-200",
  "bg-purple-400": "bg-purple-200",
  "bg-green-400": "bg-green-200",
  "bg-yellow-400": "bg-yellow-200",
  "bg-blue-400": "bg-blue-200",
  "bg-red-400": "bg-red-200",
  "bg-pink-400": "bg-pink-200",
  "bg-gray-400": "bg-gray-200",
  "bg-cyan-400": "bg-cyan-200",
  "bg-teal-400": "bg-teal-200",
  "bg-emerald-400": "bg-emerald-200",
  "bg-lime-400": "bg-lime-200",
  "bg-amber-400": "bg-amber-200",
  "bg-rose-400": "bg-rose-200",
  "bg-violet-400": "bg-violet-200",
  "bg-sky-400": "bg-sky-200",
  "bg-fuchsia-400": "bg-fuchsia-200",
  "bg-zinc-400": "bg-zinc-200",
  "bg-gray-500": "bg-gray-300",
};

// 1) Normalise la valeur provenant de la DB ou d’un label
export function normalizeTwBg(value) {
  const v = (value || "").trim();
  if (!v) return "bg-gray-200";
  if (v.startsWith("bg-")) return v; // cas DB: "bg-indigo-400"
  return LABEL_TO_BG[v] || "bg-gray-200";
}

// 2) Version "light" de la classe (ta technique 400 -> 200)
export function getLightBgClass(value) {
  const bg = normalizeTwBg(value);
  return LIGHT_BG_MAP[bg] || "bg-gray-200";
}

// 3) Couleur de texte lisible selon la famille/teinte
export function textColorForBg(twBg) {
  const fam = twBg.match(/bg-([a-z]+)-/i)?.[1] ?? "gray";
  const shade = parseInt(twBg.match(/-(\d{2,3})$/)?.[1] ?? "400", 10);
  const lightFamilies = new Set(["yellow", "lime", "amber", "orange", "green", "emerald", "teal", "cyan", "blue", "indigo", "violet", "purple", "fuchsia", "pink", "rose", "gray", "zinc", "slate"]);
  const isLight = lightFamilies.has(fam) ? shade <= 500 : shade <= 300;
  return isLight ? "text-slate-900" : "text-white";
}

// 4) Helper “pastille” pour un objet groupe { color_groupe, nom, ... }
export function getGroupPillClasses(group) {
  // ⚠️ Ton champ est bien "color_groupe"
  const bg = normalizeTwBg(group?.color_groupe);
  const text = textColorForBg(bg);
  const ring = "ring-1 ring-black/5 dark:ring-white/10";
  return { bg, text, ring };
}

// =========================
// Règles d'âge des groupes
// =========================

/** Extrait l'année de naissance (int) à partir d'une date ISO ou timestamp */
export function getBirthYear(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return Number.isFinite(d.getTime()) ? d.getFullYear() : null;
}

/** Date de référence pour l'âge : 31/12 de l'année de fin de saison si "YYYY-YYYY", sinon aujourd'hui */
export function getSeasonRefDate(seasonActive) {
  const m = /^(\d{4})\s*-\s*(\d{4})$/.exec(seasonActive || "");
  if (m) {
    const endYear = parseInt(m[2], 10);
    return new Date(endYear, 11, 31); // 31 décembre fin de saison
  }
  return new Date();
}

/** Âge révolu à la date de référence */
export function ageOn(dateStr, refDate) {
  const d = new Date(dateStr);
  if (!Number.isFinite(d.getTime())) return null;
  let age = refDate.getFullYear() - d.getFullYear();
  const m = refDate.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && refDate.getDate() < d.getDate())) age--;
  return age;
}

/**
 * Teste si un birthYear/date_naissance satisfait une règle "annees_naissance".
 * Règles supportées (au choix, séparées éventuellement par "/") :
 *  - "2021" (année exacte)
 *  - "2020-2021" (plage inclusive)
 *  - "2015-2014-2013-2012" (liste d'années séparées par "-")
 *  - "+14 ans" ou "+14ans"
 */
export function matchBirthRule(dateNaissance, annees_naissance, seasonActive) {
  if (!annees_naissance) return true; // pas de règle => on considère valide
  if (!dateNaissance) return true; // pas de DOB => ne pas spammer en rouge

  const ref = getSeasonRefDate(seasonActive);
  const y = getBirthYear(dateNaissance);
  if (y == null) return true;

  // On tolère plusieurs segments via "/"
  const segments = String(annees_naissance)
    .split("/")
    .map((s) => s.trim())
    .filter(Boolean);

  const anySegmentMatches = segments.some((frag) => {
    const clean = frag.replace(/\s+/g, "");

    // +Nans
    const mPlus = /^\+?(\d+)ans$/i.exec(clean);
    if (mPlus) {
      const min = parseInt(mPlus[1], 10);
      const a = ageOn(dateNaissance, ref);
      return a != null ? a >= min : false;
    }

    // Liste d'années hyphens longs : 2015-2014-2013
    if (/^(\d{4}-)+\d{4}$/.test(clean) && !/^\d{4}-\d{4}$/.test(clean)) {
      const years = clean.split("-").map((t) => parseInt(t, 10));
      return years.includes(y);
    }

    // Plage inclusive : 2018-2020
    if (/^\d{4}-\d{4}$/.test(clean)) {
      const [y1, y2] = clean.split("-").map((t) => parseInt(t, 10));
      const lo = Math.min(y1, y2);
      const hi = Math.max(y1, y2);
      return y >= lo && y <= hi;
    }

    // Année simple
    if (/^\d{4}$/.test(clean)) {
      return y === parseInt(clean, 10);
    }

    // Si le format est inconnu => on ne bloque pas
    return true;
  });

  return anySegmentMatches;
}

/** True si l'adhérent n'est dans AUCUN groupe compatible (utile pour le surlignage) */
export function isOutOfAllGroups(dateNaissance, groups, seasonActive) {
  const arr = Array.isArray(groups) ? groups : [];
  if (arr.length === 0) return false; // pas de groupe => pas de warning ici
  return !arr.some((g) => matchBirthRule(dateNaissance, g?.annees_naissance, seasonActive));
}
